import SignInModal from './Signin/Signin';
import SignUpModal from './Signup/Signup';
import ForgotPasswordModal from './ForgotPassword/ForgotPassword';
import ConfirmEmailModal from './ConfirmEmail/ConfirmEmail';
import SessionExpiredModal from './SessionExpired/SessionExpiredModal';

import { Form, message } from 'antd';
import { useDispatch, connect } from 'react-redux';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { confirmEmail, requestEmailReset, signIn, signUp } from '../../http/user';
import { $host } from '../../http';
import { ENDPOINTS } from '../../constants/endpoints';
import moment from 'moment';

import { useSessionTimeout } from '../../hooks/useSessionTimeout';

const AuthModalsProvider = ({
  signInDispatched,
  signUpDispatched,
  signInOpen,
  setSignInOpen,
  signUpOpen,
  setSignUpOpen,
  setLanguage
}) => {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);

  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  const { startLogoutTimer, closeSessionExpiredModal } = useSessionTimeout(
    dispatch,
    setSessionExpiredOpen,
    setSignInOpen,
    setSignUpOpen
  );

  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [confirmEmailOpen, setConfirmEmailOpen] = useState(false);

  const [signInForm] = Form.useForm();
  const [signUpForm] = Form.useForm();
  const [forgotPasswordForm] = Form.useForm();

  const [isSignInFormValid, setIsSignInFormValid] = useState(false);
  const [isSignUpFormValid, setIsSignUpFormValid] = useState(false);
  const [isForgotPasswordFormValid, setIsForgotPasswordFormValid] = useState(false);

  const [code, setCode] = useState('');

  const onSignInChange = () => {
    setIsSignInFormValid(
      signInForm.isFieldsTouched(true) &&
        signInForm.getFieldsError().every((field) => !field.errors.length)
    );
  };

  const onSignUpChange = () => {
    const allFieldsTouched =
      signUpForm.isFieldTouched('email') &&
      signUpForm.isFieldTouched('password') &&
      signUpForm.isFieldTouched('firstname') &&
      signUpForm.isFieldTouched('lastname') &&
      signUpForm.isFieldTouched('agreement') &&
      signUpForm.isFieldTouched('dateOfBirth');

    setIsSignUpFormValid(
      allFieldsTouched &&
        signUpForm.getFieldsError().every((field) => !field.errors.length)
    );
  };

  const onForgotPasswordChange = () => {
    setIsForgotPasswordFormValid(
      forgotPasswordForm.getFieldsError().every((field) => !field.errors.length)
    );
  };

  const onConfirmEmailChange = (val) => setCode(val);

  const onSignInSubmit = async (values) => {
    try {
      setLoading(true);
      const user = await signInDispatched(values);
      i18n.changeLanguage(user.language);
      setLanguage(user.language);
      setSignInOpen(false);
      startLogoutTimer();
    } catch (error) {
      if (error?.statusCode === 403) {
        await $host.post(ENDPOINTS.AUTH.SEND_EMAIL);
        messageApi.open({
          type: 'error',
          content: t('auth.pleaseConfirmEmail') || 'Please confirm your email'
        });
        setConfirmEmailOpen(true);
        setSignInOpen(false);
      } else {
        messageApi.open({
          type: 'error',
          content: error?.message ?? 'Failed to sign in'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onSignUpSubmit = async (values) => {
  try {
    setLoading(true);

    const payload = {
      email: values.email,
      password: values.password,
      first_name: values.firstname,
      last_name: values.lastname,
      marketing_consent: values.marketConsent || false,
      language: i18n.language,
      dob: moment(new Date(values.dateOfBirth)).format('YYYY-MM-DD'),
    };

    const addressFields = ['country', 'city', 'street', 'house', 'flat', 'zip'];
    addressFields.forEach((field) => {
      if (values[field]?.trim?.()) {
        payload[field] = values[field];
      }
    });

    await signUpDispatched(payload);
    setConfirmEmailOpen(true);
    setSignUpOpen(false);
    startLogoutTimer();
  } catch (error) {
    messageApi.open({
      key: 'updatable',
      type: 'error',
      content: error.message,
      duration: 2,
    });
  } finally {
    setLoading(false);
  }
};


  const onForgotPasswordSubmit = async () => {
    setLoading(true);
    try {
      const email = forgotPasswordForm.getFieldValue('email');
      await dispatch(requestEmailReset(email));
    } catch (e) {
      messageApi.open({
        key: 'updatable',
        type: 'error',
        content: e.message,
        duration: 2
      });
      forgotPasswordForm.resetFields(['email']);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const onConfirmEmailSubmit = async () => {
    try {
      setLoading(true);
      await dispatch(confirmEmail(code));
      messageApi.open({
        type: 'success',
        content: t('auth.emailConfirmed')
      });
      setConfirmEmailOpen(false);
      signUpForm.resetFields();
      setCode('');
    } catch (e) {
      messageApi.open({
        type: 'error',
        content: e.message ?? 'Failed to confirm email'
      });
    } finally {
      setLoading(false);
    }
  };

  const onSignUp = () => {
    setSignInOpen(false);
    setSignUpOpen(true);
    signInForm.resetFields();
  };

  const onSignIn = () => {
    setSignUpOpen(false);
    setSignInOpen(true);
    signUpForm.resetFields();
  };

  const onSignInClose = () => {
    setSignInOpen(false);
    signInForm.resetFields();
  };

  const onSignUpClose = () => {
    setSignUpOpen(false);
    signUpForm.resetFields();
  };

  const onForgotPasswordClose = () => {
    setForgotPasswordOpen(false);
    forgotPasswordForm.resetFields();
  };

  const onForgotPassword = () => {
    setSignInOpen(false);
    setForgotPasswordOpen(true);
  };

  const onHome = () => {
    setForgotPasswordOpen(false);
    setSignInOpen(true);
  };

  const onConfirmEmailClose = () => {
    setConfirmEmailOpen(false);
    setCode('');
  };

  return (
    <>
      {contextHolder}

      <SignInModal
        open={signInOpen}
        onSubmit={onSignInSubmit}
        onClose={onSignInClose}
        isValid={isSignInFormValid}
        onChange={onSignInChange}
        onSignUp={onSignUp}
        form={signInForm}
        onForgotPassword={onForgotPassword}
        loading={loading}
      />

      <SignUpModal
        open={signUpOpen}
        onSubmit={onSignUpSubmit}
        onClose={onSignUpClose}
        isValid={isSignUpFormValid}
        onChange={onSignUpChange}
        onSignIn={onSignIn}
        form={signUpForm}
        loading={loading}
      />

      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onChange={onForgotPasswordChange}
        onSubmit={onForgotPasswordSubmit}
        onClose={onForgotPasswordClose}
        isValid={isForgotPasswordFormValid}
        form={forgotPasswordForm}
        onHome={onHome}
      />

      <ConfirmEmailModal
        open={confirmEmailOpen}
        onChange={onConfirmEmailChange}
        onSubmit={onConfirmEmailSubmit}
        onClose={onConfirmEmailClose}
        onHome={onHome}
        code={code}
        loading={loading}
      />

      <SessionExpiredModal
        open={sessionExpiredOpen}
        onClose={closeSessionExpiredModal}
      />
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  signInDispatched: ({ email, password }) => dispatch(signIn({ email, password })),
  signUpDispatched: (body) => dispatch(signUp(body)),
  setLanguage: (language) =>
    dispatch({ type: 'App/SET_LANG', payload: language })
});

export default connect(null, mapDispatchToProps)(AuthModalsProvider);
